import React from 'react';
import NavigationContext from '../../contexts/navigation-context';
import ActiveApplicationContext from '../../contexts/active-application-context';
import vhtLogo from '../../static/images/mindful-reversed-RGB.svg';
import I18n from '../i18n';
import Button from '../button';
import Icon from '../icon';
import useIdentity from '../../hooks/use-identity';
import OrganizationSelectionModal from '../organization-selection-modal';
import config from '../../config';

export default function Header() {
  const [settingsMenuOpen, setSettingsMenuOpen] = React.useState(false);
  const {open: isNavigationOpen, setOpen: setNavigationOpen} = React.useContext(NavigationContext);
  const {application} = React.useContext(ActiveApplicationContext);
  const {user, logout, changeOrganization} = useIdentity();
  const [isOrganizationsModalVisible, setIsOrganizationsModalVisible] = React.useState(false);
  const hideOrganizationsModal = React.useCallback(() => setIsOrganizationsModalVisible(false));
  const profileUrl = `${config?.platformManagement?.uiUrl || ''}/profile`;
  const organizationName = user?.current_organization_name || '';

  const openOrganizationsModal = React.useCallback(() => {
    if (user.organizations.length > 1) {
      setIsOrganizationsModalVisible(true);
    }
  });

  const switchOrganization = React.useCallback(async organization => {
    setIsOrganizationsModalVisible(false);
    await changeOrganization(organization.id);
  });

  const editUserProfile = React.useCallback(() => {
    window.location = profileUrl;
  });

  // this handles a click handler outside the component to close the menu (clicking outside the menu closes it)
  const closeSettingsMenu = clickEvent => {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();
    setSettingsMenuOpen(false);
  };

  async function onSignOut() {
    logout();
  }

  React.useEffect(() => {
    if (settingsMenuOpen) {
      document.addEventListener('click', closeSettingsMenu);
    }

    return () => {
      document.removeEventListener('click', closeSettingsMenu);
    };
  }, [settingsMenuOpen]);

  return (
    <header className="application-header">
      <div className="header-wrapper">
        <Button className="navToggle" icon="bars" onClick={() => setNavigationOpen(!isNavigationOpen)} />

        <img className="vht-logo" src={vhtLogo} aria-hidden="true" alt="VHT" />
        <span className="current-app-name">{I18n.t(application.name)}</span>

        <span
          className={`settings-menu-button ${settingsMenuOpen ? 'active' : ''}`}
          aria-hidden="true"
          onClick={() => setSettingsMenuOpen(previous => !previous)}
        >
          <span className="avatar" />
          <span name="username" className="user-name">
            {user && user.name}
          </span>
          <span className="menu-open-arrow" />
        </span>

        <span className="org-menu-button" aria-hidden="true" onClick={openOrganizationsModal}>
          <span name="orgname" className="user-name">
            {organizationName}
          </span>
          {Object.keys(user?.organizations || {}).length > 1 && <span className="menu-open-arrow" />}
        </span>
      </div>

      <div className={`settings-menu ${settingsMenuOpen ? 'active' : ''}`}>
        <div className="user-account-list">
          <ul>
            <li id="edit-current-user" onClick={editUserProfile}>
              <span className="icon-wrapper">
                <Icon icon="cogs" />
              </span>
              <I18n t="my-account" />
            </li>
            <li id="sign-out" onClick={onSignOut}>
              <span className="icon-wrapper">
                <Icon icon="sign-out-alt" />
              </span>
              <I18n t="action.logout" />
            </li>
          </ul>
        </div>
      </div>
      <OrganizationSelectionModal
        show={isOrganizationsModalVisible}
        organizations={user.organizations ?? []}
        currentOrganization={user.current_organization_id}
        onClose={hideOrganizationsModal}
        onChange={switchOrganization}
      />
    </header>
  );
}
